// src/CalendarPage.js

import React, { useEffect, useState } from "react";
import { Calendar, dateFnsLocalizer } from "react-big-calendar";
import format from "date-fns/format";
import parse from "date-fns/parse";
import startOfWeek from "date-fns/startOfWeek";
import getDay from "date-fns/getDay";
import it from "date-fns/locale/it";
import "react-big-calendar/lib/css/react-big-calendar.css";

import CustomToolbar from "./components/CustomToolbar";
import CustomEvent from "./components/CustomEvent";

const API_URL = process.env.REACT_APP_API_URL;
const locales = {
  "it": it,
};

const localizer = dateFnsLocalizer({
  format,
  parse,
  startOfWeek,
  getDay,
  locales,
});

function CalendarContent() {
  const [events, setEvents] = useState([]);
  const [currentDate, setCurrentDate] = useState(new Date());
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [authStatus, setAuthStatus] = useState({ authorized: null, loading: true });
  const [error, setError] = useState(null);

  const handleSelectEvent = (event) => {
    setSelectedEvent(event);
    setDialogOpen(true);
  };

  useEffect(() => {
    checkAuthStatus();
  }, []);

  const checkAuthStatus = async () => {
    try {
      const token = localStorage.getItem("token");
      const response = await fetch(`${API_URL}/auth/status`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (response.ok) {
        const data = await response.json();
        setAuthStatus({ authorized: data.authorized, loading: false });
        
        if (data.authorized) {
          fetchEvents();
        }
      } else {
        setAuthStatus({ authorized: false, loading: false });
      }
    } catch (error) {
      console.error("Errore controllo autorizzazione:", error);
      setAuthStatus({ authorized: false, loading: false });
    }
  };

  const startGoogleAuth = async () => {
    try {
      const token = localStorage.getItem("token");
      const response = await fetch(`${API_URL}/auth/google`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (response.ok) {
        const data = await response.json();
        // Apri la finestra di autorizzazione Google
        window.open(data.authorization_url, '_blank');
        
        // Controlla periodicamente se l'autorizzazione è completata
        const checkInterval = setInterval(async () => {
          await checkAuthStatus();
          if (authStatus.authorized) {
            clearInterval(checkInterval);
          }
        }, 2000);
        
        // Ferma il controllo dopo 5 minuti
        setTimeout(() => clearInterval(checkInterval), 300000);
      }
    } catch (error) {
      console.error("Errore avvio autorizzazione:", error);
      setError("Errore nell'avvio dell'autorizzazione Google");
    }
  };

  const fetchEvents = async () => {
    const token = localStorage.getItem("token");
    setError(null);

    try {
      const response = await fetch(`${API_URL}/calendar/events`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        if (response.status === 401) {
          setAuthStatus({ authorized: false, loading: false });
          return;
        }
        const errorData = await response.json();
        throw new Error(errorData.detail || "Errore nel recupero eventi");
      }

      const data = await response.json();
      console.log("Eventi ricevuti:", data); // Debug

      const eventsFormatted = data.map((event) => ({
        id: event.id,
        title: event.title,
        start: new Date(event.start),
        end: new Date(event.end),
        allDay: false,
        description: event.description,
        meetLink: event.meet_link || null,
        leadName: event.lead_name || null,
      }));

      setEvents(eventsFormatted);
    } catch (error) {
      console.error("Errore nel recupero eventi:", error);
      setError(error.message);
    }
  };

  const handleNavigate = (newDate) => {
    setCurrentDate(newDate);
  };

  const handleDeleteEvent = async (eventId) => {
    const token = localStorage.getItem("token");
    try {
      await fetch(`${API_URL}/calendar/delete-event/${eventId}`, {
        method: "DELETE",
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      setEvents(events.filter((e) => e.id !== eventId));
      setDialogOpen(false);
    } catch (error) {
      console.error("Errore eliminazione evento:", error);
    }
  };

  // Loading
  if (authStatus.loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4">Verifica autorizzazione...</p>
        </div>
      </div>
    );
  }

  // Not authorized
  if (!authStatus.authorized) {
    return (
      <div className="p-4">
        <div className="max-w-md mx-auto bg-yellow-50 border border-yellow-200 rounded-lg p-6 text-center">
          <h2 className="text-xl font-bold mb-4">🔒 Autorizzazione Necessaria</h2>
          <p className="mb-4">
            Per visualizzare e gestire gli eventi del calendario, è necessario autorizzare l'accesso a Google Calendar.
          </p>
          <button
            onClick={startGoogleAuth}
            className="bg-blue-600 text-white px-6 py-2 rounded hover:bg-blue-700"
          >
            🔗 Autorizza Google Calendar
          </button>
          <p className="text-sm text-gray-600 mt-4">
            Verrà aperta una nuova finestra per l'autorizzazione Google.
          </p>
        </div>
      </div>
    );
  }

  // Authorized - show calendar
  return (
    <div className="p-4">
      <div className="flex justify-between items-center mb-4">
        <h1 className="text-2xl font-bold">Calendario Appuntamenti</h1>
        <div className="flex gap-2">
          <button
            onClick={fetchEvents}
            className="bg-green-600 text-white px-4 py-2 rounded hover:bg-green-700"
          >
            🔄 Aggiorna
          </button>
          <button
            onClick={checkAuthStatus}
            className="bg-gray-600 text-white px-4 py-2 rounded hover:bg-gray-700"
          >
            🔍 Verifica Auth
          </button>
        </div>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 rounded p-4 mb-4">
          <p className="text-red-800">❌ {error}</p>
        </div>
      )}

      <Calendar
        localizer={localizer}
        events={events}
        startAccessor="start"
        endAccessor="end"
        date={currentDate}
        onNavigate={handleNavigate}
        style={{ height: 700 }}
        views={['month', 'week', 'day', 'agenda']}
        defaultView="week"
        popup
        components={{
          toolbar: CustomToolbar,
          event: CustomEvent,
        }}
        onSelectEvent={handleSelectEvent}
      />

      {dialogOpen && selectedEvent && (
        <div className="fixed top-0 left-0 w-full h-full bg-black bg-opacity-40 flex justify-center items-center z-50">
          <div className="bg-white p-6 rounded shadow w-full max-w-md">
            <h2 className="text-xl font-bold mb-2">{selectedEvent.title}</h2>
            {selectedEvent.description && <p className="mb-2">{selectedEvent.description}</p>}
            {selectedEvent.leadName && <p className="mb-2">🔗 Lead: {selectedEvent.leadName}</p>}
            {selectedEvent.meetLink && (
              <a href={selectedEvent.meetLink} target="_blank" rel="noreferrer" className="text-blue-600 underline block mb-2">
                🎥 Partecipa al Meet
              </a>
            )}
            <div className="flex justify-end gap-4 mt-4">
              
              <button
                onClick={() => setDialogOpen(false)}
                className="bg-gray-300 px-4 py-2 rounded hover:bg-gray-400"
              >
                Chiudi
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default function CalendarPage() {
  return <CalendarContent />;
}